// src/pages/AccountPage.tsx - Forcing update
import { useState, useEffect, Fragment } from "react";
import { useAuth } from "../context/AuthContext";
import { Disclosure, Transition } from '@headlessui/react';
import { AccountPluginItemSkeleton } from "../components/account/AccountPluginItemSkeleton";
import { StarRatingInput } from "../components/common/StarRatingInput";
import { LicenseModal } from "../components/account/LicenseModal";
import { DownloadModal } from "../components/account/DownloadModal";
import { PluginIconPlaceholder } from "../components/common/PluginIconPlaceholder";
import { AddFundsModal } from "../components/account/AddFundsModal";
import { APP_CONFIG } from "../config/constants";
import { EditProfileModal } from "../components/account/EditProfileModal";
import apiClient from '../api/client';

// Tipo estendido para incluir o array de versões e purchaseId que vem da API
interface Purchase {
    purchaseId: number;
    purchaseDate: string;
    purchasePrice: number;
}

interface Plugin {
    id: string;
    name: string;
    iconUrl?: string;
    purchases: Purchase[]; // Array de compras do mesmo plugin
    versions?: {
        id: number;
        version: string;
        changelog: string;
        createdAt: string;
        fileUrl: string;
    }[];
}

interface Transaction {
    id: string;
    date: string;
    description: string;
    amount: number;
}

interface UserReview {
    pluginId: string | null;
    rating: number;
    text: string;
}

interface TopBuyer {
    rank: number;
    username: string;
    totalLicenses: number;
    uniquePlugins: number;
    totalSpent: number;
}

interface ProfileData {
    avatarUrl?: string | null;
    discordUsername?: string | null;
    linkingCode?: string | null;
}

// Função para formatar valor monetário com separador de milhar
const formatCurrency = (value: number) => {
    return value.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

// Helper para formatar a data no padrão brasileiro
const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} às ${hours}h e ${minutes}min`;
};

const ChevronUpIcon = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
);

// Sub-componente para cada item de plugin na lista
function OwnedPluginItem({ plugin, initialReview }: { plugin: Plugin; initialReview?: UserReview }) {
    const [rating, setRating] = useState(initialReview?.rating || 0);
    const [reviewText, setReviewText] = useState(initialReview?.text || "");
    const [status, setStatus] = useState({ loading: false, error: '', success: '' });
    const [isLicenseModalOpen, setIsLicenseModalOpen] = useState(false);
    const [isDownloadModal, setIsDownloadModal] = useState(false);

    const MAX_REVIEW_LENGTH = 150;

    const handleRatingSubmit = async () => {
        if (rating === 0) {
            setStatus({ loading: false, error: 'Por favor, selecione de 1 a 5 estrelas.', success: '' });
            return;
        }
        setStatus({ loading: true, error: '', success: '' });
        try {
            await apiClient.post('/reviews', {
                pluginId: plugin.id,
                rating,
                text: reviewText
            });
            setStatus({ loading: false, error: '', success: 'Obrigado por avaliar!' });
        } catch (err: any) {
            setStatus({ loading: false, error: err.response?.data?.error || "Falha ao enviar avaliação.", success: '' });
        }
    };

    // Pega a versão mais recente para exibir no card
    const latestVersion = plugin.versions && plugin.versions.length > 0 ? plugin.versions[0] : null;
    const purchaseDate = plugin.purchases[0]?.purchaseDate ? new Date(plugin.purchases[0].purchaseDate) : null;
    const purchaseCount = plugin.purchases.length;

    return (
        <>
            <LicenseModal isOpen={isLicenseModalOpen} closeModal={() => setIsLicenseModalOpen(false)} plugin={plugin} />
            <DownloadModal isOpen={isDownloadModal} closeModal={() => setIsDownloadModal(false)} plugin={plugin} />

            <div className="group relative bg-gradient-to-br from-white to-gray-50 rounded-xl border border-gray-200 hover:border-brand-blue/50 shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden">
                {/* Efeito de brilho no hover */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />

                <div className="relative p-6">
                    {/* Header do Card */}
                    <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-6 mb-4">
                        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 flex-1 w-full text-center sm:text-left">
                            {/* Informações do Plugin */}
                            <div className="flex-1 min-w-0 w-full">
                                <h3 className="font-bold text-xl text-gray-900 mb-1 truncate">{plugin.name}</h3>
                                <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 text-sm">
                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 font-medium">
                                        <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                        </svg>
                                        v{latestVersion?.version || 'N/A'}
                                    </span>
                                    {purchaseCount > 1 && (
                                        <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-purple-100 text-purple-700 font-medium">
                                            <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                            </svg>
                                            {purchaseCount} licenças
                                        </span>
                                    )}
                                    {purchaseDate && (
                                        <span className="text-gray-500 flex items-center justify-center">
                                            <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                            {purchaseDate.toLocaleDateString('pt-BR')}
                                        </span>
                                    )}
                                    {plugin.purchases[0]?.purchasePrice && (
                                        <span className="text-green-600 font-semibold flex items-center justify-center">
                                            <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            R$ {plugin.purchases[0].purchasePrice.toFixed(2).replace('.', ',')}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Botões de Ação */}
                        <div className="flex items-center gap-2 w-full sm:w-auto">
                            <button
                                onClick={() => setIsLicenseModalOpen(true)}
                                className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 bg-white border-2 border-gray-200 text-gray-700 font-semibold px-4 py-2.5 rounded-lg text-sm hover:border-brand-blue hover:text-brand-blue hover:bg-blue-50 transition-all duration-200 shadow-sm hover:shadow-md group/btn"
                            >
                                <svg className="w-4 h-4 group-hover/btn:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                                <span className="whitespace-nowrap">Licença</span>
                            </button>
                            <button
                                onClick={() => setIsDownloadModal(true)}
                                className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 bg-gradient-to-r from-brand-blue to-blue-600 text-white font-semibold px-5 py-2.5 rounded-lg text-sm hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 group/btn"
                            >
                                <svg className="w-4 h-4 group-hover/btn:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                                <span>Download</span>
                            </button>
                        </div>
                    </div>

                    {/* Seção de Avaliação */}
                    <Disclosure as="div" className="mt-4 pt-4 border-t border-gray-200">
                        {({ open }) => (
                            <>
                                <Disclosure.Button className="flex justify-between items-center w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors group/disclosure">
                                    <span className="text-sm font-semibold text-gray-700 group-hover/disclosure:text-brand-blue flex items-center gap-2">
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                        </svg>
                                        {initialReview ? 'Editar sua avaliação' : 'Avaliar este plugin'}
                                    </span>
                                    <ChevronUpIcon className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
                                </Disclosure.Button>

                                <Transition as={Fragment} enter="transition duration-200 ease-out" enterFrom="transform scale-95 opacity-0" enterTo="transform scale-100 opacity-100" leave="transition duration-150 ease-in" leaveFrom="transform scale-100 opacity-100" leaveTo="transform scale-95 opacity-0">
                                    <Disclosure.Panel as="div" className="mt-4 px-3 pb-2 space-y-4">
                                        <div className="bg-gradient-to-br from-gray-50 to-white p-4 rounded-lg border border-gray-200">
                                            <StarRatingInput rating={rating} setRating={setRating} />
                                            <div className="relative mt-3">
                                                <textarea
                                                    value={reviewText}
                                                    onChange={(e) => setReviewText(e.target.value)}
                                                    maxLength={MAX_REVIEW_LENGTH}
                                                    rows={3}
                                                    placeholder="Compartilhe sua experiência com este plugin..."
                                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-transparent resize-none transition-all"
                                                    disabled={!!status.success}
                                                />
                                                <div className={`text-xs mt-1 text-right ${reviewText.length > MAX_REVIEW_LENGTH * 0.9 ? 'text-orange-600 font-semibold' : 'text-gray-500'}`}>
                                                    {reviewText.length} / {MAX_REVIEW_LENGTH} caracteres
                                                </div>
                                            </div>
                                            {status.error && (
                                                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                                                    <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    <p className="text-sm text-red-700">{status.error}</p>
                                                </div>
                                            )}
                                            {status.success && (
                                                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
                                                    <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    <p className="text-sm text-green-700">{status.success}</p>
                                                </div>
                                            )}
                                            <button
                                                onClick={handleRatingSubmit}
                                                disabled={status.loading || !!status.success}
                                                className="mt-3 w-full bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-2.5 rounded-lg font-semibold hover:from-green-600 hover:to-green-700 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 disabled:transform-none"
                                            >
                                                {status.loading ? (
                                                    <span className="flex items-center justify-center gap-2">
                                                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        Enviando...
                                                    </span>
                                                ) : status.success ? '✓ Avaliação Salva!' : (initialReview ? 'Atualizar Avaliação' : 'Enviar Avaliação')}
                                            </button>
                                        </div>
                                    </Disclosure.Panel>
                                </Transition>
                            </>
                        )}
                    </Disclosure>
                </div>
            </div>
        </>
    );
}


export default function AccountPage() {
    const { token, user, refreshUser } = useAuth();
    const [ownedPlugins, setOwnedPlugins] = useState<Plugin[]>([]);
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [myReviews, setMyReviews] = useState<UserReview[]>([]);
    const [topBuyers, setTopBuyers] = useState<TopBuyer[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isAddFundsModalOpen, setIsAddFundsModalOpen] = useState(false);
    const [isEditProfileModalOpen, setIsEditProfileModalOpen] = useState(false);
    const [profileData, setProfileData] = useState<ProfileData>({});

    const [storeRating, setStoreRating] = useState(0);
    const [storeFeedback, setStoreFeedback] = useState("");
    const [storeReviewStatus, setStoreReviewStatus] = useState({ loading: false, error: '', success: '' });

    const MAX_REVIEW_LENGTH = 140;

    // Paginação de plugins
    const [currentPage, setCurrentPage] = useState(1);
    const pluginsPerPage = 4;
    const totalPages = Math.max(1, Math.ceil(ownedPlugins.length / pluginsPerPage));
    const startIndex = (currentPage - 1) * pluginsPerPage;
    const endIndex = startIndex + pluginsPerPage;
    const currentPlugins = ownedPlugins.slice(startIndex, endIndex);

    // Garantir que a página atual esteja dentro do intervalo quando os dados mudarem
    useEffect(() => {
        if (currentPage > totalPages) setCurrentPage(totalPages);
        if (currentPage < 1) setCurrentPage(1);
    }, [totalPages, currentPage]);

    // Paginação de transações
    const [currentTransactionPage, setCurrentTransactionPage] = useState(1);
    const transactionsPerPage = 5;
    const totalTransactionPages = Math.max(1, Math.ceil(transactions.length / transactionsPerPage));
    const transactionStartIndex = (currentTransactionPage - 1) * transactionsPerPage;
    const transactionEndIndex = transactionStartIndex + transactionsPerPage;
    const currentTransactions = transactions.slice(transactionStartIndex, transactionEndIndex);

    // Garantir que a página de transações esteja dentro do intervalo quando os dados mudarem
    useEffect(() => {
        if (currentTransactionPage > totalTransactionPages) setCurrentTransactionPage(totalTransactionPages);
        if (currentTransactionPage < 1) setCurrentTransactionPage(1);
    }, [totalTransactionPages, currentTransactionPage]);

    useEffect(() => {
        if (!token) {
            setLoading(false);
            return;
        }

        async function fetchData() {
            try {
                setLoading(true);
                setError(null);

                const [purchasesResponse, transactionsResponse, reviewsResponse, profileResponse] = await Promise.all([
                    apiClient.get('/account/purchases'),
                    apiClient.get('/account/transactions'),
                    apiClient.get('/reviews'),
                    apiClient.get('/profile')
                ]);

                // Buscar top compradores da rota pública
                const topBuyersResponse = await apiClient.get('/storefront/top-buyers').catch(() => ({ data: [] }));

                try {
                    // Group purchases by pluginId
                    const purchasesByPlugin: { [pluginId: string]: any[] } = {};

                    purchasesResponse.data?.forEach((purchase: any) => {
                        if (!purchasesByPlugin[purchase.pluginId]) {
                            purchasesByPlugin[purchase.pluginId] = [];
                        }
                        purchasesByPlugin[purchase.pluginId].push(purchase);
                    });

                    // Map grouped purchases to the Plugin interface format
                    const processedPlugins = Object.values(purchasesByPlugin).map((purchases: any[]) => {
                        const firstPurchase = purchases[0]; // Use first purchase for plugin info
                        return {
                            id: firstPurchase.pluginId,
                            name: firstPurchase.pluginName,
                            iconUrl: firstPurchase.iconUrl,
                            versions: firstPurchase.versions || [],
                            purchases: purchases.map((p: any) => ({
                                purchaseId: p.purchaseId,
                                purchaseDate: p.purchaseDate,
                                purchasePrice: p.purchasePrice
                            })).sort((a: Purchase, b: Purchase) =>
                                new Date(b.purchaseDate).getTime() - new Date(a.purchaseDate).getTime()
                            )
                        };
                    });

                    setTopBuyers(topBuyersResponse.data || []);

                    setOwnedPlugins(processedPlugins);
                    setTransactions(transactionsResponse.data || []);
                    setMyReviews(reviewsResponse.data || []);
                    setProfileData(profileResponse.data || {});
                } catch (err) {
                    console.error('Erro ao processar dados:', err);
                    setError('Erro ao processar os dados recebidos. Por favor, tente novamente.');
                }

                // Set store review if it exists
                const storeReview = reviewsResponse.data?.find((r: UserReview) => r.pluginId === null);
                if (storeReview) {
                    setStoreRating(storeReview.rating);
                    setStoreFeedback(storeReview.text);
                }
            } catch (err: any) {
                setError(err.response?.data?.error || 'Não foi possível carregar seus dados.');
            } finally {
                setLoading(false);
            }
        }

        fetchData();
    }, [token]);

    const handleProfileUpdated = async () => {
        try {
            const profileResponse = await apiClient.get('/profile');
            setProfileData(profileResponse.data || {});
        } catch (err) {
            console.error('Erro ao recarregar perfil:', err);
        }
    };

    const handleStoreReviewSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (storeRating === 0) {
            setStoreReviewStatus({ ...storeReviewStatus, error: 'Por favor, selecione de 1 a 5 estrelas.'});
            return;
        }
        setStoreReviewStatus({ loading: true, error: '', success: '' });
        try {
            await apiClient.post('/reviews', {
                pluginId: null,
                rating: storeRating,
                text: storeFeedback
            });
            setStoreReviewStatus({ loading: false, error: '', success: 'Obrigado pelo seu feedback!' });
        } catch (err: any) {
            setStoreReviewStatus({ loading: false, error: err.response?.data?.error || err.message, success: '' });
        }
    };

    const storeReview = myReviews.find(r => r.pluginId === null);
    const userBalance = user?.balance ?? 0.00;
    const hasCompletedPurchases = ownedPlugins.length > 0;

    // Helper: gerar itens de paginação com no máximo `maxVisible` botões numéricos
    function getPaginationItems(totalPages: number, currentPage: number, maxVisible = 4) {
        const items: Array<number | string> = [];
        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) items.push(i);
            return items;
        }

        // sempre tentaremos mostrar o primeiro e o último (reservando 2 slots)
        const slotsForMiddle = Math.max(0, maxVisible - 2); // normalmente 2 quando maxVisible=4

        // calcular janela centrada em currentPage com tamanho slotsForMiddle
        let start = currentPage - Math.floor(slotsForMiddle / 2);
        let end = start + slotsForMiddle - 1;

        if (start < 2) {
            start = 2;
            end = start + slotsForMiddle - 1;
        }
        if (end > totalPages - 1) {
            end = totalPages - 1;
            start = end - slotsForMiddle + 1;
            if (start < 2) start = 2;
        }

        // Primeiro sempre
        items.push(1);

        // Reticência entre 1 e start, se necessário
        if (start > 2) items.push('start-ellipsis');

        // Middle pages
        for (let p = start; p <= end; p++) items.push(p);

        // Reticência entre end e last
        if (end < totalPages - 1) items.push('end-ellipsis');

        // Último sempre
        items.push(totalPages);

        return items;
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-50">
            <AddFundsModal
                isOpen={isAddFundsModalOpen}
                closeModal={() => setIsAddFundsModalOpen(false)}
                onSuccess={refreshUser}
            />
            <EditProfileModal
                isOpen={isEditProfileModalOpen}
                onClose={() => setIsEditProfileModalOpen(false)}
                onProfileUpdated={handleProfileUpdated}
            />

            {/* Header com gradiente - Melhorado */}
            <div className="bg-gradient-to-r from-brand-blue via-blue-600 to-blue-700 text-white shadow-xl relative overflow-hidden">
                {/* Padrão decorativo de fundo */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute -top-24 -right-24 w-96 h-96 bg-white rounded-full"></div>
                    <div className="absolute -bottom-32 -left-32 w-80 h-80 bg-white rounded-full"></div>
                </div>

                <div className="container mx-auto px-4 py-16 relative">
                    <div className="max-w-6xl mx-auto">
                        <div className="flex flex-col md:flex-row items-center md:justify-between gap-6 text-center sm:text-left">
                            <div className="flex flex-col sm:flex-row items-center gap-5 w-full">
                                {/* Avatar Icon */}
                                <div className="relative">
                                    <div className="absolute inset-0 bg-white/30 rounded-3xl blur-xl"></div>
                                    <div className="relative w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-2xl border-2 border-white/30 overflow-hidden">
                                        {profileData.avatarUrl ? (
                                            <img
                                                src={`${APP_CONFIG.uploadsBaseUrl}${profileData.avatarUrl}`}
                                                alt="Avatar"
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                            </svg>
                                        )}
                                    </div>
                                </div>

                                {/* Texto do Header */}
                                <div className="flex-1 min-w-0">
                                    <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-2 drop-shadow-xl tracking-tight">
                                        Minha Conta
                                    </h1>
                                    <p className="text-blue-50 text-base sm:text-lg font-medium">
                                        Olá, <span className="font-bold text-white">{user?.name || 'Usuário'}</span>! 👋
                                    </p>
                                    <p className="text-blue-200 text-xs sm:text-sm mt-1">
                                        Gerencie seus plugins, licenças e downloads
                                    </p>
                                </div>
                            </div>

                            {/* Botão Editar Perfil */}
                            <button
                                onClick={() => setIsEditProfileModalOpen(true)}
                                className="w-full md:w-auto justify-center bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white font-semibold px-6 py-3 rounded-xl border-2 border-white/30 hover:border-white/50 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center gap-2 group shrink-0"
                            >
                                <svg className="w-5 h-5 group-hover:rotate-12 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                <span>Editar Perfil</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Ondinha decorativa na parte inferior */}
                <div className="absolute bottom-0 left-0 right-0">
                    <svg className="w-full h-6 text-gray-50" preserveAspectRatio="none" viewBox="0 0 1200 120" fill="currentColor">
                        <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" opacity=".25"></path>
                        <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" opacity=".5"></path>
                        <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z"></path>
                    </svg>
                </div>
            </div>

            <div className="container mx-auto px-4 py-12">
                <div className="max-w-6xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Coluna Principal */}
                        <div className="lg:col-span-2 space-y-6">
                            {/* Seção de Plugins */}
                            <section className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
                                <div className="bg-gradient-to-r from-gray-50 to-white px-6 py-5 border-b border-gray-200">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-brand-blue/10 rounded-lg flex items-center justify-center">
                                            <svg className="w-6 h-6 text-brand-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h2 className="text-2xl font-bold text-gray-900">Meus Plugins</h2>
                                            <p className="text-sm text-gray-500">{ownedPlugins.length} {ownedPlugins.length === 1 ? 'plugin' : 'plugins'} no total</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-6">
                                    <div className="space-y-4">
                                        {loading ? (
                                            Array.from({ length: 3 }).map((_, i) => <AccountPluginItemSkeleton key={i} />)
                                        ) : error ? (
                                            <div className="text-center py-16">
                                                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                                    <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                </div>
                                                <p className="text-red-600 font-semibold">{error}</p>
                                            </div>
                                        ) : ownedPlugins.length === 0 ? (
                                            <div className="text-center py-16">
                                                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                                    <svg className="w-10 h-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                                    </svg>
                                                </div>
                                                <p className="text-gray-500 font-medium mb-2">Nenhum plugin ainda</p>
                                                <p className="text-gray-400 text-sm">Explore nossa loja e adquira plugins incríveis!</p>
                                            </div>
                                        ) : (
                                            <>
                                                {currentPlugins.map(plugin => {
                                                    const initialReview = myReviews.find(r => r.pluginId === plugin.id);
                                                    return <OwnedPluginItem key={plugin.id} plugin={plugin} initialReview={initialReview} />;
                                                })}

                                                {/* Controles de Paginação */}
                                                {totalPages > 1 && (
                                                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-gray-200 text-center sm:text-left">
                                                        <div className="text-sm text-gray-600">
                                                            Mostrando <span className="font-semibold">{startIndex + 1}</span> a{' '}
                                                            <span className="font-semibold">{Math.min(endIndex, ownedPlugins.length)}</span> de{' '}
                                                            <span className="font-semibold">{ownedPlugins.length}</span> plugins
                                                        </div>

                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                                                disabled={currentPage === 1}
                                                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-all"
                                                            >
                                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                                                </svg>
                                                                <span className="hidden sm:inline">Anterior</span>
                                                            </button>

                                                            <div className="flex items-center gap-1">
                                                                {getPaginationItems(totalPages, currentPage, 4).map((item, idx) => {
                                                                    if (typeof item === 'string' && item.includes('ellipsis')) {
                                                                        return (
                                                                            <span key={`el-${idx}`} className="px-3 text-gray-400" aria-hidden="true">…</span>
                                                                        );
                                                                    }
                                                                    const page = Number(item);
                                                                    return (
                                                                        <button
                                                                            key={page}
                                                                            onClick={() => setCurrentPage(page)}
                                                                            aria-current={currentPage === page ? 'page' : undefined}
                                                                            aria-label={`Página ${page}${currentPage === page ? ' (Página atual)' : ''}`}
                                                                            className={`w-10 h-10 rounded-lg font-semibold transition-all ${
                                                                                currentPage === page
                                                                                    ? 'bg-brand-blue text-white shadow-md'
                                                                                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                                                            }`}
                                                                        >
                                                                            {page}
                                                                        </button>
                                                                    );
                                                                })}
                                                            </div>

                                                            <button
                                                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                                                disabled={currentPage === totalPages}
                                                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-all"
                                                            >
                                                                <span className="hidden sm:inline">Próximo</span>
                                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </section>

                            {/* Seção de Transações */}
                            <section className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
                                <div className="bg-gradient-to-r from-gray-50 to-white px-6 py-5 border-b border-gray-200">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-brand-blue/10 rounded-lg flex items-center justify-center">
                                            <svg className="w-6 h-6 text-brand-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h2 className="text-2xl font-bold text-gray-900">Histórico de Transações</h2>
                                            <p className="text-sm text-gray-500">{transactions.length} {transactions.length === 1 ? 'transação' : 'transações'} no total</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-6">
                                    {loading ? (
                                        <div className="space-y-3">
                                            {Array.from({ length: 5 }).map((_, i) => (
                                                <div key={i} className="animate-pulse flex justify-between items-center py-3">
                                                    <div className="flex-1">
                                                        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                                                        <div className="h-3 bg-gray-100 rounded w-1/2"></div>
                                                    </div>
                                                    <div className="h-6 bg-gray-200 rounded w-20"></div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : transactions.length === 0 ? (
                                        <div className="text-center py-12">
                                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                                <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                            </div>
                                            <p className="text-gray-500 text-sm">Nenhuma transação registrada</p>
                                        </div>
                                    ) : (
                                        <>
                                            <ul className="divide-y divide-gray-100">
                                                {currentTransactions.map(t => (
                                                    <li key={t.id} className="flex justify-between items-center py-4 hover:bg-gray-50 px-3 -mx-3 rounded-lg transition-colors">
                                                        <div className="flex items-start gap-3 flex-1 min-w-0">
                                                            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${t.amount < 0 ? 'bg-red-100' : 'bg-green-100'}`}>
                                                                {t.amount < 0 ? (
                                                                    <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                                                                    </svg>
                                                                ) : (
                                                                    <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                                                                    </svg>
                                                                )}
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="font-semibold text-gray-900 truncate">{t.description}</p>
                                                                <p className="text-sm text-gray-500">{formatDate(t.date)}</p>
                                                            </div>
                                                        </div>
                                                        <span className={`font-bold text-lg whitespace-nowrap ml-4 ${t.amount < 0 ? 'text-red-600' : 'text-green-600'}`}>
                                                            {t.amount < 0 ? '-' : '+'} R$ {Math.abs(t.amount).toFixed(2).replace('.',',')}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>

                                            {/* Controles de Paginação de Transações */}
                                            {totalTransactionPages > 1 && (
                                                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 mt-6 border-t border-gray-200 text-center sm:text-left">
                                                    <div className="text-sm text-gray-600">
                                                        Mostrando <span className="font-semibold">{transactionStartIndex + 1}</span> a{' '}
                                                        <span className="font-semibold">{Math.min(transactionEndIndex, transactions.length)}</span> de{' '}
                                                        <span className="font-semibold">{transactions.length}</span> transações
                                                    </div>

                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => setCurrentTransactionPage(prev => Math.max(1, prev - 1))}
                                                            disabled={currentTransactionPage === 1}
                                                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-all"
                                                        >
                                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                                            </svg>
                                                            <span className="hidden sm:inline">Anterior</span>
                                                        </button>

                                                        <div className="flex items-center gap-1">
                                                            {getPaginationItems(totalTransactionPages, currentTransactionPage, 4).map((item, idx) => {
                                                                if (typeof item === 'string' && item.includes('ellipsis')) {
                                                                    return (
                                                                        <span key={`t-el-${idx}`} className="px-3 text-gray-400" aria-hidden="true">…</span>
                                                                    );
                                                                }
                                                                const page = Number(item);
                                                                return (
                                                                    <button
                                                                        key={page}
                                                                        onClick={() => setCurrentTransactionPage(page)}
                                                                        aria-current={currentTransactionPage === page ? 'page' : undefined}
                                                                        aria-label={`Página ${page}${currentTransactionPage === page ? ' (Página atual)' : ''}`}
                                                                        className={`w-10 h-10 rounded-lg font-semibold transition-all ${
                                                                            currentTransactionPage === page
                                                                                ? 'bg-brand-blue text-white shadow-md'
                                                                                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                                                        }`}
                                                                    >
                                                                        {page}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>

                                                        <button
                                                            onClick={() => setCurrentTransactionPage(prev => Math.min(totalTransactionPages, prev + 1))}
                                                            disabled={currentTransactionPage === totalTransactionPages}
                                                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-all"
                                                        >
                                                            <span className="hidden sm:inline">Próximo</span>
                                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </section>
                        </div>
                        {/* Sidebar */}
                        <aside className="lg:col-span-1 space-y-6">
                            {/* Card de Saldo na Carteira */}
                            <div className="relative bg-gradient-to-br from-brand-blue via-blue-600 to-blue-700 rounded-2xl shadow-2xl overflow-hidden">
                                {/* Padrão de fundo decorativo */}
                                <div className="absolute inset-0 opacity-10">
                                    <div className="absolute top-0 right-0 w-40 h-40 bg-white rounded-full -mr-20 -mt-20"></div>
                                    <div className="absolute bottom-0 left-0 w-32 h-32 bg-white rounded-full -ml-16 -mb-16"></div>
                                </div>

                                <div className="relative p-6">
                                    <div className="flex items-center gap-2 mb-4">
                                        <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                                            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                            </svg>
                                        </div>
                                        <p className="text-sm font-semibold text-white/90">Saldo Disponível</p>
                                    </div>

                                    <div className="mb-6">
                                        <div className="flex items-baseline gap-2">
                                            <span className="text-white/80 text-xl font-medium">R$</span>
                                            <span className="text-5xl font-extrabold text-white drop-shadow-lg">
                                                {formatCurrency(userBalance)}
                                            </span>
                                        </div>
                                    </div>

                                    <button
                                        onClick={() => setIsAddFundsModalOpen(true)}
                                        className="w-full bg-white text-brand-blue font-bold py-3 px-4 rounded-xl hover:bg-blue-50 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center justify-center gap-2 group"
                                    >
                                        <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                        </svg>
                                        Adicionar Saldo
                                    </button>
                                </div>
                            </div>

                            {/* Formulário de Avaliação da Loja */}
                            <form onSubmit={handleStoreReviewSubmit} className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
                                <div className="bg-gradient-to-r from-yellow-50 to-orange-50 px-6 py-5 border-b border-gray-200">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                                            <svg className="w-6 h-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h2 className="text-xl font-bold text-gray-900">
                                                {storeReview ? 'Sua Avaliação' : 'Avalie a Loja'}
                                            </h2>
                                            <p className="text-xs text-gray-600">Seu feedback importa!</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-6 space-y-4">
                                    {!hasCompletedPurchases ? (
                                        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                                            <div className="flex items-start gap-3">
                                                <svg className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                <div>
                                                    <h3 className="font-semibold text-yellow-900 mb-1">Compre para avaliar</h3>
                                                    <p className="text-sm text-yellow-700">
                                                        Você precisa ter pelo menos uma compra para avaliar nossa loja. Explore nossos plugins e faça sua primeira compra!
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="bg-gradient-to-br from-gray-50 to-white p-4 rounded-xl border border-gray-200">
                                                <div className="flex justify-center mb-4">
                                                    <StarRatingInput rating={storeRating} setRating={setStoreRating} />
                                                </div>
                                                <div className="relative">
                                                    <textarea
                                                        value={storeFeedback}
                                                        onChange={(e) => setStoreFeedback(e.target.value)}
                                                        maxLength={MAX_REVIEW_LENGTH}
                                                        rows={4}
                                                        placeholder="Conte-nos sobre sua experiência..."
                                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent resize-none transition-all text-sm"
                                                        disabled={!!storeReviewStatus.success}
                                                    />
                                                    <div className={`text-xs mt-1 text-right ${storeFeedback.length > MAX_REVIEW_LENGTH * 0.9 ? 'text-orange-600 font-semibold' : 'text-gray-500'}`}>
                                                        {storeFeedback.length} / {MAX_REVIEW_LENGTH} caracteres
                                                    </div>
                                                </div>
                                            </div>

                                            {storeReviewStatus.error && (
                                                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                                                    <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    <p className="text-sm text-red-700">{storeReviewStatus.error}</p>
                                                </div>
                                            )}

                                            {storeReviewStatus.success && (
                                                <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
                                                    <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    <p className="text-sm text-green-700">{storeReviewStatus.success}</p>
                                                </div>
                                            )}

                                            <button
                                                type="submit"
                                                disabled={storeReviewStatus.loading || !!storeReviewStatus.success}
                                                className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-bold py-3 rounded-xl hover:from-yellow-600 hover:to-orange-600 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 disabled:transform-none flex items-center justify-center gap-2"
                                            >
                                                {storeReviewStatus.loading ? (
                                                    <>
                                                        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        Enviando...
                                                    </>
                                                ) : storeReviewStatus.success ? (
                                                    <>
                                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                        Avaliação Enviada!
                                                    </>
                                                ) : (
                                                    <>
                                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                                        </svg>
                                                        {storeReview ? 'Atualizar Avaliação' : 'Enviar Avaliação'}
                                                    </>
                                                )}
                                            </button>
                                        </>
                                    )}
                                </div>
                            </form>

                            {/* Top Compradores */}
                            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
                                <div className="bg-gradient-to-r from-purple-50 to-pink-50 px-6 py-5 border-b border-gray-200">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                                            <svg className="w-6 h-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h2 className="text-xl font-bold text-gray-900">Top Compradores</h2>
                                            <p className="text-xs text-gray-600">Hall da fama</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-6">
                                    {loading ? (
                                        <div className="space-y-3">
                                            {Array.from({ length: 5 }).map((_, i) => (
                                                <div key={i} className="animate-pulse flex items-center gap-3 p-3">
                                                    <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                                                    <div className="flex-1">
                                                        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                                                        <div className="h-3 bg-gray-100 rounded w-1/2"></div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : topBuyers.length === 0 ? (
                                        <div className="text-center py-8">
                                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                                <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 009.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                                </svg>
                                            </div>
                                            <p className="text-gray-500 text-sm">Nenhum comprador ainda</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            {topBuyers.map((buyer) => {
                                                const isCurrentUser = user?.name === buyer.username;
                                                const medalColors = {
                                                    1: 'from-yellow-400 to-yellow-600',
                                                    2: 'from-gray-300 to-gray-500',
                                                    3: 'from-orange-400 to-orange-600'
                                                };
                                                const medalEmoji = { 1: '🥇', 2: '🥈', 3: '🥉' };

                                                return (
                                                    <div
                                                        key={buyer.rank}
                                                        className={`group relative p-3 rounded-xl transition-all ${
                                                            isCurrentUser 
                                                                ? 'bg-gradient-to-r from-purple-100 to-pink-100 border-2 border-purple-300 shadow-md' 
                                                                : 'bg-gradient-to-br from-gray-50 to-white hover:from-purple-50 hover:to-pink-50 border border-gray-200 hover:border-purple-200'
                                                        }`}
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            {/* Rank Badge */}
                                                            <div className={`relative w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                                                                buyer.rank <= 3 
                                                                    ? `bg-gradient-to-br ${medalColors[buyer.rank as 1 | 2 | 3]} text-white shadow-lg`
                                                                    : 'bg-gray-200 text-gray-600'
                                                            }`}>
                                                                {buyer.rank <= 3 ? (
                                                                    <span className="text-xl">{medalEmoji[buyer.rank as 1 | 2 | 3]}</span>
                                                                ) : (
                                                                    <span className="font-bold text-sm">#{buyer.rank}</span>
                                                                )}
                                                            </div>

                                                            {/* User Info */}
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-2">
                                                                    <p className={`font-bold truncate ${
                                                                        isCurrentUser ? 'text-purple-900' : 'text-gray-900'
                                                                    }`}>
                                                                        {buyer.username}
                                                                    </p>
                                                                    {isCurrentUser && (
                                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-600 text-white">
                                                                            Você
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div className="flex items-center gap-3 mt-1">
                                                                    <span className="text-xs text-gray-600 flex items-center gap-1">
                                                                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                                                                        </svg>
                                                                        {buyer.totalLicenses} {buyer.totalLicenses === 1 ? 'licença' : 'licenças'}
                                                                    </span>
                                                                    <span className="text-xs text-gray-500 flex items-center gap-1">
                                                                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                                                        </svg>
                                                                        {buyer.uniquePlugins} {buyer.uniquePlugins === 1 ? 'plugin' : 'plugins'}
                                                                    </span>
                                                                    <span className="text-xs font-semibold text-green-600">
                                                                        R$ {buyer.totalSpent.toFixed(2).replace('.', ',')}
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            {/* Trophy Icon for top 3 */}
                                                            {buyer.rank <= 3 && (
                                                                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                    <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                                                                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                                                    </svg>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Vinculação Discord */}
                            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
                                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 px-6 py-5 border-b border-gray-200">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-indigo-500/20 rounded-lg flex items-center justify-center">
                                            <svg className="w-6 h-6 text-indigo-600" fill="currentColor" viewBox="0 0 24 24" stroke="currentColor">
                                                <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419-.0189 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z"/>
                                            </svg>
                                        </div>
                                        <div>
                                            <h2 className="text-xl font-bold text-gray-900">Discord</h2>
                                            <p className="text-xs text-gray-600">Vincule sua conta</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-6">
                                    {profileData.discordUsername ? (
                                        <div className="text-center">
                                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                                <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            </div>
                                            <p className="text-lg font-semibold text-gray-900 mb-1">Conta Vinculada</p>
                                            <p className="text-sm text-gray-600">@{profileData.discordUsername}</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            <div className="text-center">
                                                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                                    <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419-.0189 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z"/>
                                                    </svg>
                                                </div>
                                                <p className="text-lg font-semibold text-gray-900 mb-2">Vincule seu Discord</p>
                                                <p className="text-sm text-gray-600 mb-4">
                                                    Vincule sua conta do Discord para acessar comandos especiais no nosso servidor.
                                                </p>
                                            </div>

                                            <div className="bg-gradient-to-br from-gray-50 to-white p-4 rounded-xl border border-gray-200">
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="text-sm font-medium text-gray-700">Código de Vinculação:</span>
                                                    <button
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(profileData.linkingCode || '');
                                                            // You could add a toast notification here
                                                        }}
                                                        className="text-xs bg-indigo-100 hover:bg-indigo-200 text-indigo-700 px-2 py-1 rounded-md font-medium transition-colors flex items-center gap-1"
                                                    >
                                                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                        </svg>
                                                        Copiar
                                                    </button>
                                                </div>
                                                <div className="bg-white border border-gray-300 rounded-lg px-3 py-2 font-mono text-sm text-gray-800 select-all">
                                                    {profileData.linkingCode || 'XXXX-XXXX-XXXX-XXXX'}
                                                </div>
                                            </div>

                                            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                                                <div className="flex items-start gap-3">
                                                    <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    <div>
                                                        <h3 className="font-semibold text-blue-900 mb-1">Como vincular</h3>
                                                        <ol className="text-sm text-blue-700 space-y-1">
                                                            <li>1. Copie o código acima</li>
                                                            <li>2. Vá para o Discord e digite <code className="bg-blue-100 px-1 rounded">/vincular {profileData.linkingCode || 'XXXX-XXXX-XXXX-XXXX'}</code></li>
                                                            <li>3. Sua conta será vinculada automaticamente</li>
                                                        </ol>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </aside>
                    </div>
                </div>
            </div>
        </div>
    );
}

